import { useCallback, useEffect, useRef } from 'react'

// Types
import { QuestionnaireAnswer, QuestionnaireEntry } from 'common/types'

// Context
import { useEditDocumentedListContext } from 'lib/context/EditDocumentedListContext/EditDocumentedListContext'

// Other
import dayjs from 'lib/dayjs'
import { useForm } from 'react-hook-form'

export const questions = [
  {
    id: 'mdBroughtImplant',
    question: 'Did the surgeon provide any implants for this case?',
    defaultValue: null,
    isCheckable: true,
  },
  {
    id: 'isProductRecall',
    question:
      'Were any of the implants used in this case a replacement due to a product manufacturer recall?',
    defaultValue: null,
    isCheckable: true,
  },
  {
    id: 'isProductMalfunction',
    question:
      'Were any of the implants used in this case a replacement due to a product malfunction or defect?',
    defaultValue: null,
    isCheckable: true,
  },
] as const

export const useQuestionsBoxLogic = () => {
  const { setQuestionnaire, triggerFormSubmit, triggerSubmitResolverRef } =
    useEditDocumentedListContext()
  const prevTriggerRef = useRef(0)

  const {
    control,
    watch,
    setValue,
    formState: { errors },
    handleSubmit,
    trigger,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      mdBroughtImplant: null,
      isProductRecall: null,
      isProductMalfunction: null,
      adverseEvent: null,
      implantSource: null,
      sourceFacilityNotified: null,
      notificationDate: null,
      notificationTime: null,
    },
  })

  const formValues = watch()

  // Function to create questionnaire entries from form values
  const createQuestionnaireEntries = (values: typeof formValues) => {
    const questionEntries: QuestionnaireEntry[] = [
      {
        question: questions[0].question,
        answer: values.mdBroughtImplant as QuestionnaireAnswer,
      },
      {
        question: questions[1].question,
        answer: values.isProductRecall as QuestionnaireAnswer,
      },
      {
        question: questions[2].question,
        answer: values.isProductMalfunction as QuestionnaireAnswer,
      },
      {
        question: 'Adverse Event Identification',
        answer: values.adverseEvent as QuestionnaireAnswer,
      },
    ]

    if (values.adverseEvent === 'YES') {
      // Add implant source
      if (values.implantSource) {
        questionEntries.push({
          question: 'Implant Source',
          answer: values.implantSource as QuestionnaireAnswer,
        })
      }

      // Add source facility notification with date and time if YES
      let facilityAnswer: QuestionnaireAnswer =
        values.sourceFacilityNotified as QuestionnaireAnswer

      if (
        values.sourceFacilityNotified === 'YES' &&
        values.notificationDate &&
        values.notificationTime
      ) {
        const formattedDate = dayjs(values.notificationDate).format(
          'MM/DD/YYYY'
        )
        const formattedTime = dayjs(values.notificationTime).format('HH:mm')
        facilityAnswer =
          `YES, ${formattedDate}, ${formattedTime}` as QuestionnaireAnswer
      }

      questionEntries.push({
        question: 'Source Facility Notified',
        answer: facilityAnswer,
      })
    }

    return questionEntries
  }

  // Validate form before submission
  const validateForm = useCallback(async () => {
    // Validate all fields
    const isValid = await trigger()
    // Call hasErrors with the opposite of isValid
    return isValid
  }, [trigger])

  const handleSubmitForm = handleSubmit(() => {
    const entries = createQuestionnaireEntries(formValues)
    setQuestionnaire({
      questions: entries,
    })
  })

  useEffect(() => {
    if (triggerFormSubmit !== prevTriggerRef.current) {
      prevTriggerRef.current = triggerFormSubmit
      validateForm().then((isValid) => {
        if (isValid) {
          handleSubmitForm()
        }
        // Use the shared ref from context
        triggerSubmitResolverRef.current?.(isValid)
      })
    }
  }, [
    triggerFormSubmit,
    handleSubmitForm,
    validateForm,
    triggerSubmitResolverRef,
  ])

  return {
    control,
    watch,
    setValue,
    formState: { errors },
    values: formValues,
    handleSubmitForm,
  }
}
